import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  ShoppingCart,
  History,
  FileText,
  Loader2,
  ScanBarcode,
  AlertTriangle,
  Calendar,
  Search,
  X,
} from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Select } from "../../components/ui/Select"
import { fr, paymentMethodLabel } from "../../locales/fr"
import { getMembers } from "../../services/memberService"
import {
  getProducts,
  getProductByBarcode,
  createProduct,
  updateProduct,
  deleteProduct,
} from "../../services/productService"
import { getProductSales, createProductSale, downloadProductSaleInvoice } from "../../services/productSaleService"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table"
import { Badge } from "../../components/ui/Badge"
import { EmptyState } from "../../components/ui/EmptyState"

const EXPIRY_WARN_DAYS = 30
/** Stock ≤ seuil (et > 0) = alerte « stock faible » */
const STOCK_LOW_THRESHOLD = 5

function formatMoney(n) {
  const x = Number(n)
  if (Number.isNaN(x)) return "—"
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "MAD" }).format(x)
}

function parseYmd(s) {
  if (!s) return null
  const d = new Date(String(s) + "T12:00:00")
  return Number.isNaN(d.getTime()) ? null : d
}

function formatShortDate(iso) {
  const d = parseYmd(iso)
  if (!d) return "—"
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short" }).format(d)
}

const emptyProductForm = () => ({
  name: "",
  description: "",
  unit_price: "",
  purchase_price: "",
  active: true,
  barcode: "",
  expiry_date: "",
  track_stock: false,
  stock_quantity: "",
})

const emptyCartLine = () => ({ product_id: "", quantity: "1" })

function maxQtyForProductRow(product, productsById) {
  if (!product) return 99999
  const pr = typeof product === "number" ? productsById.get(product) : product
  if (!pr) return 99999
  if (!pr.track_stock) return 99999
  return Math.max(0, Number(pr.stock_quantity) || 0)
}

export default function Products() {
  const p = fr.productsPage
  const [tab, setTab] = useState("catalog")

  const [products, setProducts] = useState([])
  const [sales, setSales] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saleSubmitting, setSaleSubmitting] = useState(false)
  const [scanLoading, setScanLoading] = useState(false)
  const [saleSearchQuery, setSaleSearchQuery] = useState("")
  const [productModalOpen, setProductModalOpen] = useState(false)

  const [productForm, setProductForm] = useState(emptyProductForm)
  const [editingId, setEditingId] = useState(null)

  const [memberId, setMemberId] = useState("")
  const [buyerName, setBuyerName] = useState("")
  const [method, setMethod] = useState("cash")
  const [invoiceNotes, setInvoiceNotes] = useState("")
  const [discountMad, setDiscountMad] = useState("")
  const [cartLines, setCartLines] = useState([emptyCartLine()])
  const scanRef = useRef(null)

  const productsById = useMemo(() => new Map(products.map((x) => [x.id, x])), [products])

  const expiryAlerts = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const limit = new Date(today)
    limit.setDate(limit.getDate() + EXPIRY_WARN_DAYS)
    const rows = []
    for (const pr of products) {
      if (!pr.active || !pr.expiry_date) continue
      const exp = parseYmd(pr.expiry_date)
      if (!exp) continue
      if (exp < today) {
        const days = Math.max(0, Math.round((today - exp) / 864e5))
        rows.push({ ...pr, level: "expired", days })
      } else if (exp <= limit) {
        const days = Math.max(0, Math.ceil((exp - today) / 864e5))
        rows.push({ ...pr, level: "soon", days })
      }
    }
    rows.sort((a, b) => parseYmd(a.expiry_date) - parseYmd(b.expiry_date))
    return rows
  }, [products])

  const stockAlerts = useMemo(() => {
    const rows = []
    for (const pr of products) {
      if (!pr.active || !pr.track_stock) continue
      const q = Number(pr.stock_quantity) || 0
      if (q === 0) {
        rows.push({ ...pr, stockLevel: "out" })
      } else if (q <= STOCK_LOW_THRESHOLD) {
        rows.push({ ...pr, stockLevel: "low", qty: q })
      }
    }
    rows.sort((a, b) => {
      if (a.stockLevel !== b.stockLevel) return a.stockLevel === "out" ? -1 : 1
      return (Number(a.stock_quantity) || 0) - (Number(b.stock_quantity) || 0)
    })
    return rows
  }, [products])

  const loadProducts = useCallback(async () => {
    try {
      const data = await getProducts()
      setProducts(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error(e)
    }
  }, [])

  const loadSales = useCallback(async () => {
    try {
      const data = await getProductSales()
      setSales(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error(e)
    }
  }, [])

  const loadMembers = useCallback(async () => {
    try {
      const data = await getMembers()
      setMembers(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error(e)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      await Promise.all([loadProducts(), loadSales(), loadMembers()])
      if (!cancelled) setLoading(false)
    }
    run()
    return () => {
      cancelled = true
    }
  }, [loadProducts, loadSales, loadMembers])

  const activeProducts = useMemo(
    () => products.filter((x) => x.active),
    [products]
  )

  /** Produits vendables : actifs et pas en rupture si stock suivi */
  const activeProductsForSale = useMemo(
    () =>
      activeProducts.filter((pr) => !pr.track_stock || (Number(pr.stock_quantity) || 0) > 0),
    [activeProducts]
  )

  const saleSearchMatches = useMemo(() => {
    const q = saleSearchQuery.trim().toLowerCase()
    if (q.length < 1) return []
    return activeProductsForSale
      .filter((pr) => {
        const name = (pr.name || "").toLowerCase()
        const bc = (pr.barcode || "").toLowerCase()
        return name.includes(q) || (bc && bc.includes(q))
      })
      .slice(0, 15)
  }, [activeProductsForSale, saleSearchQuery])

  const cartQtyForProduct = useCallback(
    (productId, excludeLineIndex = -1) => {
      const pid = Number(productId)
      if (!pid) return 0
      let sum = 0
      cartLines.forEach((line, i) => {
        if (i === excludeLineIndex) return
        if (Number(line.product_id) !== pid) return
        sum += Math.max(1, parseInt(String(line.quantity), 10) || 1)
      })
      return sum
    },
    [cartLines]
  )

  const addProductToCart = useCallback(
    (product) => {
      const pid = product.id
      const maxQ = maxQtyForProductRow(product, productsById)
      const totalBefore = cartQtyForProduct(pid)
      if (product.track_stock && totalBefore >= maxQ) {
        alert(p.scanOutOfStock)
        return
      }
      setCartLines((lines) => {
        const sumOthers = (excludeIdx) =>
          lines.reduce((s, l, i) => {
            if (Number(l.product_id) !== pid || i === excludeIdx) return s
            return s + Math.max(1, parseInt(String(l.quantity), 10) || 1)
          }, 0)
        const idx = lines.findIndex((l) => Number(l.product_id) === pid)
        if (idx >= 0) {
          const next = [...lines]
          const cur = Math.max(1, parseInt(String(next[idx].quantity), 10) || 1)
          const others = sumOthers(idx)
          const newLineQty = product.track_stock ? Math.min(cur + 1, maxQ - others) : cur + 1
          next[idx] = { ...next[idx], quantity: String(newLineQty) }
          return next
        }
        const emptyIdx = lines.findIndex((l) => !l.product_id)
        if (emptyIdx >= 0) {
          const next = [...lines]
          next[emptyIdx] = { product_id: String(pid), quantity: "1" }
          return next
        }
        return [...lines, { product_id: String(pid), quantity: "1" }]
      })
    },
    [cartQtyForProduct, productsById, p.scanOutOfStock]
  )

  const saleSubtotal = useMemo(() => {
    let t = 0
    for (const line of cartLines) {
      const pid = Number(line.product_id)
      if (!pid) continue
      const pr = productsById.get(pid)
      if (!pr) continue
      const q = Math.max(1, parseInt(String(line.quantity), 10) || 1)
      t += q * Number(pr.unit_price)
    }
    return Math.round(t * 100) / 100
  }, [cartLines, productsById])

  const saleToPay = useMemo(() => {
    const sub = saleSubtotal
    const raw = parseFloat(String(discountMad).replace(",", ".")) || 0
    const d = Math.round(Math.max(0, raw) * 100) / 100
    const capped = Math.min(d, sub)
    return Math.max(0, Math.round((sub - capped) * 100) / 100)
  }, [saleSubtotal, discountMad])

  const discountEquivPercent = useMemo(() => {
    if (saleSubtotal <= 0) return null
    const raw = parseFloat(String(discountMad).replace(",", ".")) || 0
    const d = Math.min(Math.max(0, raw), saleSubtotal)
    return Math.round((d / saleSubtotal) * 10000) / 100
  }, [saleSubtotal, discountMad])

  const resetProductForm = () => {
    setProductForm(emptyProductForm())
    setEditingId(null)
  }

  const closeProductModal = () => {
    setProductModalOpen(false)
    resetProductForm()
  }

  const openNewProductModal = () => {
    resetProductForm()
    setProductModalOpen(true)
  }

  const openEditProductModal = (row) => {
    setEditingId(row.id)
    setProductForm({
      name: row.name || "",
      description: row.description || "",
      unit_price: String(row.unit_price ?? ""),
      purchase_price:
        row.purchase_price !== undefined && row.purchase_price !== null
          ? String(row.purchase_price)
          : "",
      active: row.active !== false,
      barcode: row.barcode || "",
      expiry_date: row.expiry_date ? String(row.expiry_date).slice(0, 10) : "",
      track_stock: Boolean(row.track_stock),
      stock_quantity: row.track_stock ? String(row.stock_quantity ?? "0") : "",
    })
    setProductModalOpen(true)
  }

  /** Après un scan (Entrée) : vérifie si le code est déjà pris par un autre produit. */
  const checkBarcodeConflict = useCallback(
    async (raw) => {
      const v = String(raw ?? "").trim()
      if (!v) return
      try {
        const existing = await getProductByBarcode(v)
        if (existing && (!editingId || existing.id !== editingId)) {
          alert(p.barcodeAlreadyUsed(existing.name))
          return
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          alert(err.response?.data?.error || err.message || "Erreur")
          return
        }
      }
      setProductForm((f) => ({ ...f, barcode: v }))
    },
    [editingId, p]
  )

  const submitProduct = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        name: productForm.name.trim(),
        description: productForm.description.trim() || null,
        unit_price: parseFloat(String(productForm.unit_price).replace(",", ".")) || 0,
        purchase_price: Math.max(0, parseFloat(String(productForm.purchase_price).replace(",", ".")) || 0),
        active: productForm.active,
        barcode: productForm.barcode.trim() || null,
        track_stock: productForm.track_stock,
        stock_quantity: productForm.track_stock
          ? Math.max(0, parseInt(String(productForm.stock_quantity), 10) || 0)
          : 0,
        expiry_date: productForm.expiry_date?.trim() || null,
      }
      if (editingId) {
        await updateProduct(editingId, payload)
      } else {
        await createProduct(payload)
      }
      await loadProducts()
      resetProductForm()
      setProductModalOpen(false)
    } catch (err) {
      alert(err.response?.data?.error || err.message || "Erreur")
    }
    setSaving(false)
  }

  const removeProduct = async (id) => {
    if (!window.confirm(p.confirmDelete)) return
    try {
      await deleteProduct(id)
      await loadProducts()
      if (editingId === id) closeProductModal()
    } catch (err) {
      alert(err.response?.data?.error || "Erreur")
    }
  }

  const addCartLine = () => setCartLines((lines) => [...lines, emptyCartLine()])
  const removeCartLine = (i) =>
    setCartLines((lines) => (lines.length <= 1 ? lines : lines.filter((_, j) => j !== i)))
  const setCartLine = (i, field, value) =>
    setCartLines((lines) =>
      lines.map((line, j) => (j === i ? { ...line, [field]: value } : line))
    )

  const handleScan = async (e) => {
    e.preventDefault()
    const raw = scanRef.current?.value?.trim() ?? ""
    if (!raw) return
    setScanLoading(true)
    try {
      const pr = await getProductByBarcode(raw)
      if (!pr.track_stock || (Number(pr.stock_quantity) || 0) > 0) {
        addProductToCart(pr)
      } else {
        alert(p.scanOutOfStock)
      }
      scanRef.current.value = ""
    } catch (err) {
      if (err.response?.status === 404) alert(p.scanNotFound)
      else alert(err.response?.data?.error || err.message || "Erreur")
    }
    setScanLoading(false)
    scanRef.current?.focus()
  }

  const submitSale = async (e) => {
    e.preventDefault()
    const lines = cartLines
      .filter((l) => l.product_id)
      .map((l) => ({
        product_id: Number(l.product_id),
        quantity: Math.max(1, parseInt(String(l.quantity), 10) || 1),
      }))
    if (lines.length === 0) {
      alert(p.cartEmpty)
      return
    }
    if (!memberId && buyerName.trim().length < 2) {
      alert(p.buyerRequired)
      return
    }
    setSaleSubmitting(true)
    try {
      const rawDisc = parseFloat(String(discountMad).replace(",", ".")) || 0
      const disc = Math.round(Math.min(Math.max(0, rawDisc), saleSubtotal) * 100) / 100
      const payload = {
        method,
        invoice_notes: invoiceNotes.trim() || undefined,
        discount_amount: disc,
        lines,
        ...(memberId ? { member_id: Number(memberId) } : { buyer_name: buyerName.trim() }),
      }
      const created = await createProductSale(payload)
      await loadSales()
      await loadProducts()
      setCartLines([emptyCartLine()])
      setDiscountMad("")
      setMemberId("")
      setBuyerName("")
      setInvoiceNotes("")
      setMethod("cash")
      if (created?.id) {
        await downloadProductSaleInvoice(created.id)
      }
      setTab("history")
    } catch (err) {
      alert(err.response?.data?.error || err.message || "Erreur")
    }
    setSaleSubmitting(false)
  }

  useEffect(() => {
    if (tab !== "sale") setSaleSearchQuery("")
  }, [tab])

  useEffect(() => {
    if (tab !== "sale") return
    const t = setTimeout(() => scanRef.current?.focus(), 100)
    return () => clearTimeout(t)
  }, [tab])

  useEffect(() => {
    if (!productModalOpen) return
    const onKey = (e) => {
      if (e.key === "Escape") {
        setProductModalOpen(false)
        setEditingId(null)
        setProductForm(emptyProductForm())
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [productModalOpen])

  const tabs = [
    { id: "catalog", label: p.tabCatalog, icon: Package },
    { id: "sale", label: p.tabSale, icon: ShoppingCart },
    { id: "history", label: p.tabHistory, icon: History },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{p.title}</h1>
        <p className="text-muted-foreground">{p.subtitle}</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-1">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`
              inline-flex items-center gap-2 rounded-b-lg border-b-2 px-3 py-2 text-sm font-medium transition-colors
              ${
                tab === id
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }
            `}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {(expiryAlerts.length > 0 || stockAlerts.length > 0) && (
        <div
          className="space-y-4 rounded-lg border border-border bg-muted/20 p-4 sm:p-5"
          role="region"
          aria-label={p.shopAlertsHeading}
        >
          <h2 className="text-base font-semibold text-foreground">{p.shopAlertsHeading}</h2>
          {expiryAlerts.length > 0 ? (
            <div
              className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 dark:bg-amber-950/30"
              role="status"
            >
              <div className="mb-3 flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div>
                  <p className="font-semibold text-foreground">{p.expiryAlertTitle}</p>
                  <p className="text-sm text-muted-foreground">{p.expiryAlertSubtitle}</p>
                </div>
              </div>
              <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
                {expiryAlerts.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/80 bg-background/80 px-3 py-2"
                  >
                    <span className="font-medium">{row.name}</span>
                    <span className="flex flex-wrap items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="tabular-nums text-muted-foreground">{formatShortDate(row.expiry_date)}</span>
                      {row.level === "expired" ? (
                        <Badge variant="destructive">{p.expiryExpired}</Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="border-amber-500/60 bg-amber-500/15 text-amber-900 dark:text-amber-100"
                        >
                          {p.expirySoon}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {row.level === "expired" ? p.expiryDaysAgo(row.days) : p.expiryDaysLeft(row.days)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {stockAlerts.length > 0 ? (
            <div
              className="rounded-lg border border-orange-500/40 bg-orange-500/10 p-4 dark:bg-orange-950/25"
              role="status"
            >
              <div className="mb-3 flex items-start gap-2">
                <Package className="mt-0.5 h-5 w-5 shrink-0 text-orange-600 dark:text-orange-400" />
                <div>
                  <p className="font-semibold text-foreground">{p.stockAlertTitle}</p>
                  <p className="text-sm text-muted-foreground">{p.stockAlertSubtitle}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {p.stockLowThresholdNote(STOCK_LOW_THRESHOLD)}
                  </p>
                </div>
              </div>
              <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
                {stockAlerts.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/80 bg-background/80 px-3 py-2"
                  >
                    <span className="font-medium">{row.name}</span>
                    <span className="flex flex-wrap items-center gap-2">
                      {row.stockLevel === "out" ? (
                        <Badge variant="destructive">{p.stockOut}</Badge>
                      ) : (
                        <Badge variant="warning">{p.stockLow}</Badge>
                      )}
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {p.stockUnitsLeft(Number(row.stock_quantity) || 0)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}

      {tab === "catalog" && (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle>{p.catalogList}</CardTitle>
            <Button type="button" onClick={openNewProductModal}>
              <Plus className="mr-2 h-4 w-4" />
              {p.addProduct}
            </Button>
          </CardHeader>
          <CardContent className="p-0">
              {loading ? (
                <p className="p-6 text-sm text-muted-foreground">{p.loading}</p>
              ) : products.length === 0 ? (
                <div className="flex flex-col items-center gap-4 p-8">
                  <EmptyState title={p.emptyCatalog} />
                  <Button type="button" onClick={openNewProductModal}>
                    <Plus className="mr-2 h-4 w-4" />
                    {p.addProduct}
                  </Button>
                </div>
              ) : (
                <Table className="min-w-[720px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[8rem]">{p.name}</TableHead>
                      <TableHead className="hidden sm:table-cell">{p.colBarcode}</TableHead>
                      <TableHead className="text-right">{p.unitPrice}</TableHead>
                      <TableHead className="hidden text-right md:table-cell">{p.colPurchase}</TableHead>
                      <TableHead className="hidden text-right lg:table-cell">{p.colMargin}</TableHead>
                      <TableHead className="text-right">{p.colStock}</TableHead>
                      <TableHead className="hidden sm:table-cell">{p.colExpiry}</TableHead>
                      <TableHead>{p.status}</TableHead>
                      <TableHead className="text-right">{p.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((row) => {
                      const desc = row.description != null ? String(row.description).trim() : ""
                      const showDesc =
                        desc.length > 0 && desc.toLowerCase() !== String(row.name || "").trim().toLowerCase()
                      return (
                      <TableRow key={row.id}>
                        <TableCell className="max-w-[14rem] align-top">
                          <div className="min-w-0">
                            <span className="font-medium leading-snug">{row.name}</span>
                            {showDesc ? (
                              <span className="mt-1 block text-xs leading-snug text-muted-foreground line-clamp-2">
                                {desc}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="hidden font-mono text-xs align-top sm:table-cell">
                          {row.barcode || "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums align-top">{formatMoney(row.unit_price)}</TableCell>
                        <TableCell className="hidden text-right tabular-nums text-sm align-top md:table-cell">
                          {formatMoney(row.purchase_price ?? 0)}
                        </TableCell>
                        <TableCell className="hidden text-right text-sm align-top lg:table-cell">
                          {row.margin_percent != null ? (
                            <span className="tabular-nums">
                              {formatMoney(row.margin_amount ?? 0)} ({row.margin_percent}%)
                            </span>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm align-top">
                          {row.track_stock ? row.stock_quantity : p.stockUnlimited}
                        </TableCell>
                        <TableCell className="hidden whitespace-nowrap text-sm align-top sm:table-cell">
                          {row.expiry_date ? formatShortDate(row.expiry_date) : "—"}
                        </TableCell>
                        <TableCell className="align-top">
                          {row.active ? (
                            <Badge variant="success">{p.statusActive}</Badge>
                          ) : (
                            <Badge variant="secondary">{p.statusInactive}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right align-top">
                          <Button type="button" variant="ghost" size="icon" onClick={() => openEditProductModal(row)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeProduct(row.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
        </Card>
      )}

      {productModalOpen ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            aria-label={fr.common.close}
            onClick={closeProductModal}
          />
          <Card
            className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col border-border shadow-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-modal-title"
          >
            <CardHeader className="flex shrink-0 flex-row items-start justify-between gap-2 space-y-0 pb-2">
              <div className="min-w-0 pr-2">
                <CardTitle id="product-modal-title">
                  {editingId ? p.editProduct : p.newProduct}
                </CardTitle>
                <CardDescription className="mt-2">{p.productModalHint}</CardDescription>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={closeProductModal}
                aria-label={fr.common.close}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="min-h-0 flex-1 overflow-y-auto pt-0">
              <form onSubmit={submitProduct} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{p.name}</label>
                  <Input
                    value={productForm.name}
                    onChange={(e) => setProductForm((f) => ({ ...f, name: e.target.value }))}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{p.description}</label>
                  <Input
                    value={productForm.description}
                    onChange={(e) => setProductForm((f) => ({ ...f, description: e.target.value }))}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{p.barcode}</label>
                  <Input
                    value={productForm.barcode}
                    onChange={(e) => setProductForm((f) => ({ ...f, barcode: e.target.value }))}
                    placeholder={p.barcodePlaceholder}
                    className="font-mono"
                    autoComplete="off"
                    name="product-barcode"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        void checkBarcodeConflict(e.currentTarget.value)
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground">{p.barcodeHint}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{p.expiryDate}</label>
                  <Input
                    type="date"
                    value={productForm.expiry_date}
                    onChange={(e) => setProductForm((f) => ({ ...f, expiry_date: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">{p.expiryHint}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{p.unitPrice}</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={productForm.unit_price}
                    onChange={(e) => setProductForm((f) => ({ ...f, unit_price: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{p.purchasePrice}</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={productForm.purchase_price}
                    onChange={(e) => setProductForm((f) => ({ ...f, purchase_price: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">{p.purchasePriceHint}</p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={productForm.track_stock}
                    onChange={(e) =>
                      setProductForm((f) => ({ ...f, track_stock: e.target.checked }))
                    }
                  />
                  {p.trackStock}
                </label>
                {productForm.track_stock && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{p.stockQuantity}</label>
                    <Input
                      type="number"
                      min="0"
                      value={productForm.stock_quantity}
                      onChange={(e) =>
                        setProductForm((f) => ({ ...f, stock_quantity: e.target.value }))
                      }
                    />
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={productForm.active}
                    onChange={(e) => setProductForm((f) => ({ ...f, active: e.target.checked }))}
                  />
                  {p.active}
                </label>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button type="submit" disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editingId ? p.save : p.create}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeProductModal}>
                    {p.cancel}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {tab === "sale" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                {p.saleAddSection}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <ScanBarcode className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {p.scanTitle}
                </div>
                <form onSubmit={handleScan} className="flex flex-wrap items-end gap-2">
                  <Input
                    ref={scanRef}
                    name="barcode-scan"
                    className="max-w-md font-mono"
                    placeholder={p.scanPlaceholder}
                    autoComplete="off"
                    disabled={scanLoading}
                  />
                  <Button type="submit" variant="secondary" disabled={scanLoading}>
                    {scanLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : p.scanSubmit}
                  </Button>
                </form>
                <p className="text-xs text-muted-foreground">{p.barcodeHint}</p>
              </div>

              <div className="space-y-2 border-t border-border pt-6">
                <label className="flex items-center gap-2 text-sm font-medium" htmlFor="sale-product-search">
                  <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                  {p.saleSearchLabel}
                </label>
                <Input
                  id="sale-product-search"
                  value={saleSearchQuery}
                  onChange={(e) => setSaleSearchQuery(e.target.value)}
                  placeholder={p.saleSearchPlaceholder}
                  autoComplete="off"
                  className="max-w-md"
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return
                    e.preventDefault()
                    if (saleSearchMatches.length === 1) {
                      addProductToCart(saleSearchMatches[0])
                      setSaleSearchQuery("")
                    }
                  }}
                />
                {saleSearchQuery.trim().length > 0 ? (
                  <ul
                    className="max-h-52 max-w-xl overflow-y-auto rounded-md border border-border bg-muted/30"
                    role="listbox"
                  >
                    {saleSearchMatches.length === 0 ? (
                      <li className="px-3 py-2.5 text-sm text-muted-foreground">{p.saleSearchEmpty}</li>
                    ) : (
                      saleSearchMatches.map((pr) => (
                        <li key={pr.id} className="border-b border-border/60 last:border-0">
                          <button
                            type="button"
                            className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/80"
                            onClick={() => {
                              addProductToCart(pr)
                              setSaleSearchQuery("")
                            }}
                          >
                            <span className="min-w-0 font-medium leading-snug">{pr.name}</span>
                            <span className="shrink-0 tabular-nums text-muted-foreground">
                              {formatMoney(pr.unit_price)}
                              {pr.track_stock ? ` · ${p.stockLabel} ${pr.stock_quantity}` : ""}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                ) : null}
                <p className="text-xs text-muted-foreground">{p.saleSearchHint}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{p.newSale}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitSale} className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{p.member}</label>
                    <Select value={memberId} onChange={(e) => setMemberId(e.target.value)}>
                      <option value="">{p.memberNone}</option>
                      {members.map((m) => (
                        <option key={m.id} value={String(m.id)}>
                          {m.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  {!memberId && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">{p.buyerName}</label>
                      <Input
                        value={buyerName}
                        onChange={(e) => setBuyerName(e.target.value)}
                        placeholder={p.buyerPlaceholder}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">{p.lines}</label>
                  <div className="space-y-3">
                    {cartLines.map((line, i) => {
                      const pr = line.product_id ? productsById.get(Number(line.product_id)) : null
                      const maxQ = pr
                        ? maxQtyForProductRow(pr, productsById) - cartQtyForProduct(Number(line.product_id), i)
                        : 99999
                      const cap = Math.max(1, maxQ)
                      return (
                        <div key={i} className="flex flex-wrap items-end gap-2">
                          <Select
                            value={line.product_id}
                            onChange={(e) => setCartLine(i, "product_id", e.target.value)}
                            className="min-w-[200px] flex-1"
                          >
                            <option value="">{p.pickProduct}</option>
                            {activeProductsForSale.map((x) => (
                              <option key={x.id} value={String(x.id)}>
                                {x.name} — {formatMoney(x.unit_price)}
                                {x.track_stock ? ` (${p.stockLabel}: ${x.stock_quantity})` : ""}
                              </option>
                            ))}
                          </Select>
                          <Input
                            type="number"
                            min={1}
                            max={pr ? cap : undefined}
                            className="w-24"
                            value={line.quantity}
                            onChange={(e) => {
                              let v = parseInt(e.target.value, 10) || 1
                              if (pr && pr.track_stock) v = Math.min(v, cap)
                              setCartLine(i, "quantity", v)
                            }}
                          />
                          <Button type="button" variant="outline" size="icon" onClick={() => removeCartLine(i)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addCartLine}>
                    <Plus className="mr-2 h-4 w-4" />
                    {p.addLine}
                  </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{p.method}</label>
                    <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                      <option value="cash">{paymentMethodLabel("cash")}</option>
                      <option value="card">{paymentMethodLabel("card")}</option>
                      <option value="transfer">{paymentMethodLabel("transfer")}</option>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <p className="text-xs text-muted-foreground">{p.discountHint}</p>
                    <div className="grid gap-3 rounded-lg border border-border bg-card/50 p-4 sm:grid-cols-2 lg:grid-cols-4">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">{p.subtotal}</p>
                        <p className="text-lg font-semibold tabular-nums">{formatMoney(saleSubtotal)}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">{p.discountMad}</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={discountMad}
                          onChange={(e) => setDiscountMad(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">{p.discountPercent}</label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          placeholder="0"
                          className="mt-1"
                          onBlur={(e) => {
                            const pct = parseFloat(e.target.value.replace(",", ".")) || 0
                            if (saleSubtotal <= 0) return
                            const mad = Math.round(saleSubtotal * Math.min(100, Math.max(0, pct)) * 0.01 * 100) / 100
                            setDiscountMad(String(mad))
                          }}
                        />
                        {discountEquivPercent != null && saleSubtotal > 0 ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {p.discountApproxEquiv(discountEquivPercent)}
                          </p>
                        ) : null}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">{p.amountDue}</p>
                        <p className="text-lg font-semibold tabular-nums text-primary">{formatMoney(saleToPay)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">{p.invoiceNotes}</label>
                  <textarea
                    className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={invoiceNotes}
                    onChange={(e) => setInvoiceNotes(e.target.value)}
                    placeholder={p.invoiceNotesPlaceholder}
                  />
                </div>

                <Button type="submit" disabled={saleSubmitting || saleToPay <= 0 || saleSubtotal <= 0}>
                  {saleSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShoppingCart className="mr-2 h-4 w-4" />}
                  {p.recordSale}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "history" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{p.salesHistory}</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => loadSales()}>
              {p.refresh}
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {sales.length === 0 ? (
              <div className="p-6">
                <EmptyState title={p.emptySales} />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{p.colDate}</TableHead>
                    <TableHead>{p.colInvoice}</TableHead>
                    <TableHead>{p.colClient}</TableHead>
                    <TableHead className="text-right">{p.colTotal}</TableHead>
                    <TableHead>{p.colMethod}</TableHead>
                    <TableHead className="text-right">{p.actions}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sales.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="whitespace-nowrap text-sm">{s.paid_at}</TableCell>
                      <TableCell className="font-mono text-sm">{s.invoice_number || `—`}</TableCell>
                      <TableCell>{s.member || s.buyer_name || "—"}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatMoney(s.total_amount)}
                      </TableCell>
                      <TableCell>{paymentMethodLabel(s.method)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadProductSaleInvoice(s.id)}
                        >
                          <FileText className="mr-1 h-4 w-4" />
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
